/**
 * Preferences storage utility for FileExplorer
 * 
 * Provides functions to save and load user preferences to/from localStorage.
 * Persists view mode, sort field, and sort order preferences across sessions.
 * 
 * @module PreferencesStorage
 * 
 * Requirements: 9.4, 10.4
 * - 9.4: THE FileExplorer SHALL persist the user's sort preference across sessions
 * - 10.4: THE FileExplorer SHALL persist the user's view mode preference across sessions
 */

/**
 * localStorage key for storing FileExplorer preferences
 */
export const PREFERENCES_STORAGE_KEY = 'fileExplorerPreferences';

/**
 * Valid values for viewMode preference
 */
export const VALID_VIEW_MODES = ['list', 'grid'];

/**
 * Valid values for sortBy preference
 */
export const VALID_SORT_FIELDS = ['name', 'date', 'size'];

/**
 * Valid values for sortOrder preference
 */
export const VALID_SORT_ORDERS = ['asc', 'desc'];

/**
 * Returns the default preferences object
 * 
 * Default values:
 * - viewMode: 'list' - List view is the default display format
 * - sortBy: 'name' - Files are sorted by name by default
 * - sortOrder: 'asc' - Ascending order is the default
 * 
 * @returns {Object} Default preferences object
 * @property {string} viewMode - Default view mode ('list')
 * @property {string} sortBy - Default sort field ('name')
 * @property {string} sortOrder - Default sort order ('asc')
 * 
 * @example
 * const defaults = getDefaultPreferences();
 * // { viewMode: 'list', sortBy: 'name', sortOrder: 'asc' }
 */
export function getDefaultPreferences() {
  return {
    viewMode: 'list',
    sortBy: 'name',
    sortOrder: 'asc'
  };
}

/**
 * Validates a viewMode value
 * 
 * @param {*} value - Value to validate
 * @returns {boolean} True if value is a valid viewMode
 */
function isValidViewMode(value) {
  return VALID_VIEW_MODES.includes(value);
}

/**
 * Validates a sortBy value
 * 
 * @param {*} value - Value to validate
 * @returns {boolean} True if value is a valid sortBy field
 */
function isValidSortBy(value) {
  return VALID_SORT_FIELDS.includes(value);
}

/**
 * Validates a sortOrder value
 * 
 * @param {*} value - Value to validate
 * @returns {boolean} True if value is a valid sortOrder
 */
function isValidSortOrder(value) {
  return VALID_SORT_ORDERS.includes(value);
}

/**
 * Saves user preferences to localStorage
 * 
 * Validates the preferences object before saving. Invalid values are
 * replaced with defaults. Only valid preference keys are stored.
 * 
 * Requirements: 9.4, 10.4
 * - 9.4: Persist sort preference across sessions
 * - 10.4: Persist view mode preference across sessions
 * 
 * @param {Object} preferences - Preferences object to save
 * @param {string} [preferences.viewMode] - View mode ('list' or 'grid')
 * @param {string} [preferences.sortBy] - Sort field ('name', 'date', or 'size')
 * @param {string} [preferences.sortOrder] - Sort order ('asc' or 'desc')
 * @returns {boolean} True if save was successful, false otherwise
 * 
 * @example
 * savePreferences({ viewMode: 'grid', sortBy: 'date', sortOrder: 'desc' });
 * // Returns true if saved successfully
 * 
 * @example
 * // Invalid values are replaced with defaults
 * savePreferences({ viewMode: 'invalid', sortBy: 'name', sortOrder: 'asc' });
 * // Saves { viewMode: 'list', sortBy: 'name', sortOrder: 'asc' }
 */
export function savePreferences(preferences) {
  try {
    // Handle null/undefined input
    if (!preferences || typeof preferences !== 'object') {
      return false;
    }

    const defaults = getDefaultPreferences();
    
    // Build validated preferences object
    const validatedPreferences = {
      viewMode: isValidViewMode(preferences.viewMode) 
        ? preferences.viewMode 
        : defaults.viewMode,
      sortBy: isValidSortBy(preferences.sortBy) 
        ? preferences.sortBy 
        : defaults.sortBy,
      sortOrder: isValidSortOrder(preferences.sortOrder) 
        ? preferences.sortOrder 
        : defaults.sortOrder
    };

    // Save to localStorage
    localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(validatedPreferences));
    return true;
  } catch (error) {
    // localStorage may be unavailable (e.g., private browsing, quota exceeded)
    console.error('Failed to save preferences:', error);
    return false;
  }
}

/**
 * Loads user preferences from localStorage
 * 
 * Returns stored preferences merged with defaults. If localStorage is
 * unavailable or contains invalid data, returns default preferences.
 * Individual invalid values are replaced with their defaults.
 * 
 * Requirements: 9.4, 10.4
 * - 9.4: Persist sort preference across sessions
 * - 10.4: Persist view mode preference across sessions
 * 
 * @returns {Object} Preferences object with all required fields
 * @property {string} viewMode - View mode ('list' or 'grid')
 * @property {string} sortBy - Sort field ('name', 'date', or 'size')
 * @property {string} sortOrder - Sort order ('asc' or 'desc')
 * 
 * @example
 * // If localStorage has { viewMode: 'grid', sortBy: 'date', sortOrder: 'desc' }
 * const prefs = loadPreferences();
 * // Returns { viewMode: 'grid', sortBy: 'date', sortOrder: 'desc' }
 * 
 * @example
 * // If localStorage is empty or unavailable
 * const prefs = loadPreferences();
 * // Returns { viewMode: 'list', sortBy: 'name', sortOrder: 'asc' }
 */
export function loadPreferences() {
  const defaults = getDefaultPreferences();
  
  try {
    const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    
    // Return defaults if nothing stored
    if (!stored) {
      return defaults;
    }

    // Parse stored JSON
    const parsed = JSON.parse(stored);
    
    // Handle non-object parsed values
    if (!parsed || typeof parsed !== 'object') {
      return defaults;
    }

    // Merge with defaults, validating each field
    return {
      viewMode: isValidViewMode(parsed.viewMode) 
        ? parsed.viewMode 
        : defaults.viewMode,
      sortBy: isValidSortBy(parsed.sortBy) 
        ? parsed.sortBy 
        : defaults.sortBy,
      sortOrder: isValidSortOrder(parsed.sortOrder) 
        ? parsed.sortOrder 
        : defaults.sortOrder
    };
  } catch (error) {
    // JSON parse error or localStorage unavailable
    console.error('Failed to load preferences:', error);
    return defaults;
  }
}

/**
 * Clears stored preferences from localStorage
 * 
 * Useful for resetting to defaults or during testing.
 * 
 * @returns {boolean} True if clear was successful, false otherwise
 * 
 * @example
 * clearPreferences();
 * const prefs = loadPreferences();
 * // Returns default preferences
 */
export function clearPreferences() {
  try {
    localStorage.removeItem(PREFERENCES_STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Failed to clear preferences:', error);
    return false;
  }
}
